import React, { useEffect, useState } from 'react';
import { Upload, Input, Space, Typography, message, Table, Button, Select, Divider, Tag, Card } from 'antd';
import { InboxOutlined, FileTextOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadVerificationDocument } from '@app/api/export.api';

const { Dragger } = Upload;
const { Text, Title } = Typography;

// ─── Types ───────────────────────────────────────────────────────────────────

interface SubmittedDoc {
  id?: number;
  document_id: string;
  name: string;
  file: string;
  document_url: string;
  type?: 'PR' | 'LAB';
}

type PROption = { label: string; value: number };
type LabOption = { label: string; value: number | string };

interface ExportDocumentsProps {
  export_id: string;
  editable?: boolean;
  status: string | null;

  prOptions: PROption[] | string | null;
  selectedPRs: any[] | null;
  selectedLRs: any[] | null;
  verificationDocs: {
    PR?: SubmittedDoc[];
    LAB?: SubmittedDoc[];
  };
  onPRDocChange?: (file: UploadFile, documentName: string, documentId: string) => void;
  onPRChange?: (ids: number[]) => void;

  labOptions: LabOption[] | string | null;
  selectedLabIds?: (number | string)[];
  onLabDocChange?: (file: UploadFile, documentName: string, documentId: string) => void;
  onLabChange?: (ids: (number | string)[]) => void;
  editableUser?: any;
}

// ─── Document Upload Sub-component ───────────────────────────────────────────

interface DocUploaderProps {
  export_id: string;
  editable: boolean;
  submittedDocs: SubmittedDoc[];
  onChange?: (file: UploadFile, documentName: string, documentId: string) => void;
  uploadFn: (exportId: number, formData: FormData) => Promise<any>;
  documentType: 'PR' | 'LAB';
  onDocsUpdated?: (docs: SubmittedDoc[]) => void;
  editableUser?: any;
}

const DocUploader: React.FC<DocUploaderProps> = ({
  export_id,
  editable,
  submittedDocs,
  onChange,
  uploadFn,
  documentType,
  onDocsUpdated,
  editableUser,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editingDocumentName, setEditingDocumentName] = useState<string>('');
  const [reuploadDocument, setReuploadDocument] = useState<SubmittedDoc | null>(null);
  const canEdit = editable && !!editableUser;

  const uploadDocument = async (file: File, existingDocument?: SubmittedDoc) => {
    setUploading(true);

    try {
      const formData = new FormData();

      formData.append('name', existingDocument?.name || documentName);

      if (existingDocument?.document_id) {
        formData.append('document_id', existingDocument.document_id);
      }

      formData.append('type', documentType);
      formData.append('file', file);

      const response = await uploadFn(Number(export_id), formData);

      const updatedDocs =
        response?.data?.export_verification_documents?.map((doc: any) => ({
          id: doc.id,
          document_id: doc.document_id,
          name: doc.name,
          file: doc.file,
          document_url: doc.document_url,
          type: doc.type,
        })) ?? [];

      onDocsUpdated?.(updatedDocs);

      message.success(`${file.name} uploaded successfully`);

      setDocumentName('');
      setFileList([]);
      setReuploadDocument(null);
      setEditingDocumentId(null);
    } catch {
      message.error('Failed to upload file.');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps: UploadProps = {
    multiple: false,
    beforeUpload: async (file) => {
      if (!canEdit) {
        message.error('You do not have permission to upload.');
        return Upload.LIST_IGNORE;
      }
      if (!documentName) {
        message.error('Please enter a document name first.');
        return Upload.LIST_IGNORE;
      }

      await uploadDocument(file);

      return false;
    },
    onRemove: () => setFileList([]),
    fileList,
    showUploadList: true,
  };

  const handleView = (_: string, fileUrl: string) => window.open(fileUrl, '_blank');

  const handleRename = (record: SubmittedDoc) => {
    setEditingDocumentId(record.document_id);
    setEditingDocumentName(record.name);

    setReuploadDocument(null);
  };

  const handleReUpload = (record: SubmittedDoc) => {
    setReuploadDocument(record);

    (document.getElementById(`reupload-input-${documentType}`) as HTMLInputElement)?.click();
  };

  const handleNameOnlyUpdate = async () => {
    if (!editingDocumentName || !editingDocumentId) {
      message.error('Missing document name or ID.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', editingDocumentName);
      formData.append('document_id', editingDocumentId);
      formData.append('type', documentType);
      const response = await uploadFn(Number(export_id), formData);

      const updatedDocs =
        response?.data?.export_verification_documents?.map((doc: any) => ({
          document_id: doc.document_id,
          name: doc.name,
          file: doc.file,
          document_url: doc.document_url,
        })) ?? [];

      onDocsUpdated?.(updatedDocs);
      message.success('Document name updated successfully');
      onChange?.({} as UploadFile, editingDocumentName, editingDocumentId);
      setEditingDocumentName('');
      setEditingDocumentId(null);
    } catch {
      message.error('Failed to update document name.');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    { title: 'Document Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: SubmittedDoc) => (
        <Space size="small">
          <Button onClick={() => handleView(record.document_id, record.document_url)} type="primary" size="small">
            View
          </Button>
          {canEdit && (
            <>
              <Button size="small" onClick={() => handleRename(record)}>
                Rename
              </Button>

              <Button size="small" onClick={() => handleReUpload(record)}>
                Re-upload
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {canEdit && (
        <>
          {!editingDocumentId && (
            <Input
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={uploading}
            />
          )}

          {editingDocumentId && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input value={editingDocumentName} onChange={(e) => setEditingDocumentName(e.target.value)} />

              <Space>
                <Button type="primary" onClick={handleNameOnlyUpdate} loading={uploading}>
                  Update Name
                </Button>

                <Button
                  onClick={() => {
                    setEditingDocumentId(null);
                    setEditingDocumentName('');
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Space>
          )}

          {!editingDocumentId && (
            <Dragger {...uploadProps} disabled={uploading}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to upload</p>
              <p className="ant-upload-hint">One file per document.</p>
              {documentName && (
                <Text type="secondary">
                  Document Name: <b>{documentName}</b>
                </Text>
              )}
            </Dragger>
          )}
        </>
      )}

      <input
        id={`reupload-input-${documentType}`}
        type="file"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];

          if (!file || !reuploadDocument) {
            return;
          }

          await uploadDocument(file, reuploadDocument);

          e.target.value = '';
        }}
      />

      <Table
        columns={columns}
        dataSource={submittedDocs}
        rowKey="document_id"
        pagination={false}
        size="small"
        style={{ marginTop: canEdit ? 16 : 0 }}
      />
    </Space>
  );
};

// ─── Main Combined Component ──────────────────────────────────────────────────

const ExportDocuments: React.FC<ExportDocumentsProps> = ({
  export_id,
  editable = false,
  status,
  prOptions,
  selectedPRs,
  selectedLRs,
  onPRDocChange,
  onPRChange,
  labOptions,
  verificationDocs,
  onLabDocChange,
  onLabChange,
  editableUser,
}) => {
  // ── Normalise props that may arrive as null/string before data loads ──
  const safePROptions: PROption[] = Array.isArray(prOptions) ? prOptions : [];
  const safeSelectedPRs: any[] = Array.isArray(selectedPRs) ? selectedPRs : [];
  const safeSelectedLRs: any[] = Array.isArray(selectedLRs) ? selectedLRs : [];
  const safeLabOptions: LabOption[] = Array.isArray(labOptions) ? labOptions : [];
  const [lockedLabIds, setLockedLabIds] = useState<(number | string)[]>([]);
  // ── PR selection state ──
  const [selectedPRIds, setSelectedPRIds] = useState<number[]>([]);
  const [lockedPRIds, setLockedPRIds] = useState<number[]>([]);
  const [prDocs, setPrDocs] = useState<SubmittedDoc[]>([]);
  const [labDocs, setLabDocs] = useState<SubmittedDoc[]>([]);
  // ── Lab selection state ──
  const [selectedLabValues, setSelectedLabValues] = useState<(number | string)[]>([]);

  // Initialise PR selections from selectedPRs
  useEffect(() => {
    const linked = safeSelectedPRs.map((pr) => pr.id as number);

    setSelectedPRIds(linked);
    setLockedPRIds(linked);
  }, [safeSelectedPRs]);

  // Initialise Lab selections from prop
  useEffect(() => {
    const linked = safeSelectedLRs.map((lr) => lr.id);

    setSelectedLabValues(linked);
    setLockedLabIds(linked);
  }, [safeSelectedLRs]);

  useEffect(() => {
    console.log('passed verificationDocs:', verificationDocs);
    setPrDocs(Array.isArray(verificationDocs?.PR) ? verificationDocs.PR : []);
    setLabDocs(Array.isArray(verificationDocs?.LAB) ? verificationDocs.LAB : []);
  }, [verificationDocs]);

  const handlePRChange = (values: number[]) => {
    const merged = Array.from(new Set([...lockedPRIds, ...values]));
    setSelectedPRIds(merged);
    onPRChange?.(merged); // ← bubble up
  };

  const handleLabChange = (values: (number | string)[]) => {
    const merged = Array.from(new Set([...lockedLabIds, ...values]));

    setSelectedLabValues(merged);
    onLabChange?.(merged);
  };

  // ── Shared card style ──
  const sectionCardStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    border: '1px solid #e8e8e8',
    borderRadius: 8,
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 24,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      {/* ── Product Registrations ── */}
      <Card style={sectionCardStyle} bodyStyle={{ padding: 20 }}>
        <div style={sectionTitleStyle}>
          <FileTextOutlined style={{ fontSize: 18, color: '#1677ff' }} />
          <Title level={5} style={{ margin: 0 }}>
            Product Registrations
          </Title>
        </div>

        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Dropdown / tag display */}
          {editable ? (
            <>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select Product Registrations"
                options={safePROptions}
                value={selectedPRIds}
                onChange={handlePRChange}
                tagRender={(props) => {
                  const isLocked = lockedPRIds.includes(props.value as number);
                  return (
                    <Tag
                      color={isLocked ? 'blue' : 'default'}
                      closable={!isLocked}
                      onClose={props.onClose}
                      style={{ marginRight: 4 }}
                    >
                      {props.label}
                    </Tag>
                  );
                }}
              />
            </>
          ) : (
            <Space wrap>
              {safeSelectedPRs.length === 0 ? (
                <Text type="secondary">No Product Registrations selected.</Text>
              ) : (
                safeSelectedPRs.map((pr) => (
                  <Tag key={pr.id} color="blue">
                    {pr.code}
                  </Tag>
                ))
              )}
            </Space>
          )}

          <Divider style={{ margin: '16px 0 12px' }} />

          <Text strong style={{ fontSize: 13 }}>
            Additional Documents
          </Text>
          <DocUploader
            export_id={export_id}
            editable={editable}
            editableUser={editableUser}
            submittedDocs={prDocs}
            onDocsUpdated={setPrDocs}
            onChange={onPRDocChange}
            uploadFn={uploadVerificationDocument}
            documentType="PR"
          />
        </Space>
      </Card>

      {/* ── Lab Verifications ── */}
      <Card style={sectionCardStyle} bodyStyle={{ padding: 20 }}>
        <div style={sectionTitleStyle}>
          <ExperimentOutlined style={{ fontSize: 18, color: '#52c41a' }} />
          <Title level={5} style={{ margin: 0 }}>
            Lab Verifications
          </Title>
        </div>

        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Dropdown / tag display */}
          {editable ? (
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select Lab Verifications"
              options={safeLabOptions}
              value={selectedLabValues}
              onChange={handleLabChange}
              tagRender={(props) => {
                const isLocked = lockedLabIds.includes(props.value);

                return (
                  <Tag
                    color={isLocked ? 'green' : 'default'}
                    closable={!isLocked}
                    onClose={props.onClose}
                    style={{ marginRight: 4 }}
                  >
                    {props.label}
                  </Tag>
                );
              }}
            />
          ) : (
            <Space wrap>
              {safeSelectedLRs.length === 0 ? (
                <Text type="secondary">No Lab Verifications selected.</Text>
              ) : (
                safeSelectedLRs.map((lab) => (
                  <Tag key={lab.id} color="green">
                    {lab.code}
                  </Tag>
                ))
              )}
            </Space>
          )}

          <Divider style={{ margin: '16px 0 12px' }} />

          <Text strong style={{ fontSize: 13 }}>
            Additional Documents
          </Text>
          <DocUploader
            export_id={export_id}
            editable={editable}
            editableUser={editableUser}
            submittedDocs={labDocs}
            onChange={onLabDocChange}
            uploadFn={uploadVerificationDocument}
            documentType="LAB"
            onDocsUpdated={setLabDocs}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ExportDocuments;
